/**
 * Stable, structure-preserving JSON canonicalization for round-trip
 * equality checks in the visual form-builder and workflow-builder.
 *
 * Used by both builders to satisfy the design's round-trip contract:
 * load JSON → render visually → save without changes → byte-equivalent
 * JSON modulo whitespace and key order.
 *
 * What it does
 * ------------
 *
 *   - Recursively sorts object keys alphabetically.
 *   - Preserves array order (semantic for `formSchema.fields[]` and
 *     `workflowConfig.steps[]` — reordering them changes runtime behavior).
 *   - Normalizes whitespace by re-emitting via `JSON.stringify` with no
 *     indent (callers compare the resulting strings or re-parse the result).
 *   - Leaves primitives alone (`null`, numbers, booleans, strings).
 *
 * What it does NOT do
 * -------------------
 *
 *   - Does NOT remove keys with `undefined` values that came in as explicit
 *     `undefined` (those are dropped by `JSON.stringify` automatically).
 *   - Does NOT coerce types (e.g., `1` stays `1`, `"1"` stays `"1"`).
 *   - Does NOT touch sparse-array holes (which JSON wouldn't carry anyway).
 *
 * Bounded depth: a defensive recursion cap (`MAX_DEPTH = 64`) prevents a
 * pathological cyclic input from hanging the builder. JSON has no cycles
 * by spec, so reaching the cap is itself a bug — we throw to surface it.
 *
 * The helper is intentionally FE-only. Per design open question Q3, the
 * single consumer today is the visual builder; promoting to
 * `@kfolks/core-utils` waits for a second consumer.
 */

const MAX_DEPTH = 64

export type JsonValue = null | boolean | number | string | JsonObject | JsonArray
export interface JsonObject {
	[key: string]: JsonValue
}
export type JsonArray = JsonValue[]

/**
 * Recursively produce a canonical clone of the input where every object's
 * keys are sorted alphabetically. Arrays preserve their order; primitives
 * pass through. Throws if recursion exceeds `MAX_DEPTH` (cyclic input).
 */
export function canonicalize(value: unknown, depth = 0): JsonValue {
	if (depth > MAX_DEPTH) {
		throw new Error(`canonicalize: max depth ${MAX_DEPTH} exceeded (likely cyclic input)`)
	}

	if (value === null) return null
	if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
		return value
	}
	if (typeof value === 'undefined' || typeof value === 'function' || typeof value === 'symbol') {
		// JSON would drop these; we emit `null` to keep the shape compatible
		// with `JSON.stringify` output (which omits undefined-valued object
		// properties but emits null inside arrays).
		return null
	}

	if (Array.isArray(value)) {
		return value.map((item) => canonicalize(item, depth + 1))
	}

	if (typeof value === 'object') {
		const obj = value as Record<string, unknown>
		const sortedKeys = Object.keys(obj).sort()
		const out: JsonObject = {}
		for (const key of sortedKeys) {
			const v = obj[key]
			// Skip explicit `undefined` to match `JSON.stringify` semantics
			// for object properties (it omits them entirely).
			if (typeof v === 'undefined') continue
			out[key] = canonicalize(v, depth + 1)
		}
		return out
	}

	// Unreachable for well-formed JSON-ish input. Defensive null.
	return null
}

/**
 * Stringify with canonical key ordering and no indent. Callers comparing
 * round-trip results SHOULD use `canonicalStringify(a) === canonicalStringify(b)`
 * rather than deep-equality on the parsed values, so any subtle
 * stringification differences (number → string coercions, etc.) surface.
 */
export function canonicalStringify(value: unknown): string {
	return JSON.stringify(canonicalize(value))
}

/**
 * True when two JSON-compatible values are byte-equivalent after
 * canonicalization (key-order normalized, whitespace stripped).
 * Use for round-trip equality assertions in builder tests and in the
 * "is the in-progress payload identical to the persisted payload" check
 * that drives the Save button's enabled state.
 */
export function canonicalEquals(a: unknown, b: unknown): boolean {
	return canonicalStringify(a) === canonicalStringify(b)
}
